import PropTypes from 'prop-types'
import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'

import {
  PlayerControls,
  PlayerDeck,
  PlayerProvider,
  PlayerRecording,
  PlayerSeekBar,
  PlayerTimer,
} from '../../../components/Player'

import useDeckReloadApi from './useDeckReloadApi'
import useRecordingReloadApi from './useRecordingReloadApi'

import useTeamPlayer from './useTeamPlayer'

const styles = (theme) => ({
  flexContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  deckContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2, 0),
    position: 'relative',
  },
  recordingContainer: {
    bottom: theme.spacing(4),
    position: 'absolute',
    right: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
      bottom: theme.spacing(1),
      right: theme.spacing(1),
    },
  },
  recording: {
    height: theme.spacing(16),
    width: theme.spacing(16),
    [theme.breakpoints.down('sm')]: {
      height: theme.spacing(12),
      width: theme.spacing(12),
    },
    [theme.breakpoints.down('xs')]: {
      height: theme.spacing(10),
      width: theme.spacing(10),
    },
  },
  seekBarContainer: {
    alignItems: 'center',
    display: 'flex',
    height: theme.spacing(4),
    justifyContent: 'center',
  },
  seekBarLoading: {
    maxWidth: theme.spacing(96),
    width: '100%',
  },
  controlsContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 0,
    justifyContent: 'center',
  },
  timerContainer: {
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.typography.pxToRem(40),
    height: '36px',
    margin: theme.spacing(0, 0, 0, 2),
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(32),
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  timerLoading: {
    fontSize: theme.typography.pxToRem(20),
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  dividerMargin: {
    margin: theme.spacing(2, 0),
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  action: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
})
const useStyles = makeStyles(styles)

export default function TeamPlayer(props) {
  const { authorizationToken, deckId, recordingId } = props

  const { t } = useTranslation()

  const [deck, setDeck] = useState({
    disabled: false,
    id: deckId,
    slides: [],
    status: 'pending',
    statusProgress: 0,
  })
  useDeckReloadApi(deck, { authorizationToken, setDeck })

  const [recording, setRecording] = useState({
    disabled: false,
    id: recordingId,
    status: 'pending',
  })
  useRecordingReloadApi(recording, { authorizationToken, setRecording })

  const player = useTeamPlayer({
    deckDisabled: deck.disabled,
    deckId,
    deckSlides: deck.slides,
    deckStatus: deck.status,
    deckStatusProgress: deck.statusProgress,
    recordingDeckTimestamps: recording.deckTimestamps,
    recordingDisabled: recording.disabled,
    recordingId,
    recordingMedia: recording.media,
    recordingMediaFormat: recording.mediaFormat,
    recordingStatus: recording.status,
    recordingStatusProgress: recording.statusProgress,
  })

  const classes = useStyles()

  return (
    <div className={classes.flexContainer}>
      <PlayerProvider player={player}>
        <div className={classes.deckContainer}>
          <PlayerDeck controls={!player.isPlaying} />

          {!recording.disabled && (
            <div className={classes.recordingContainer}>
              <PlayerRecording className={classes.recording} />
            </div>
          )}
        </div>

        {!recording.disabled && (
          <Fragment>
            <div className={classes.seekBarContainer}>
              {recording.status !== 'success' && (
                <LinearProgress className={classes.seekBarLoading} />
              )}

              {recording.status === 'success' && <PlayerSeekBar />}
            </div>

            <div className={classes.controlsContainer}>
              <PlayerControls />

              <div className={classes.timerContainer}>
                {recording.status !== 'success' && (
                  <span className={classes.timerLoading}>
                    {t('quickRecord:shareStep-recording-messages-isLoading')}
                  </span>
                )}

                {recording.status === 'success' && <PlayerTimer />}
              </div>
            </div>
          </Fragment>
        )}
      </PlayerProvider>
    </div>
  )
}

TeamPlayer.propTypes = {
  authorizationToken: PropTypes.string.isRequired,
  deckId: PropTypes.number.isRequired,
  recordingId: PropTypes.number.isRequired,
}
